import type { AddAddressInput, Address, Customer, Mutation } from '@sg/graphql-schema';

import { CurrentCustomerDocument } from '../../../../../context/Customer/GraphQL/CurrentCustomer.query.generated';

import type { Cache } from '@urql/exchange-graphcache';

/**
 * Changing addresses returns an address object.
 * This is fine for when the address is updated,
 * But if it is added or removed, this is needed to update
 * the current customer's object's addresses in URQL cache.
 *
 * Additionally, when we add an address without a name,
 * it's a different operation under the same mutation,
 * as the resulting address is provisional and
 * won't be available in `customer.addresses`.
 *
 * Finally, the `updateAddress` can be called with a
 * provisional address, so it must also add the address
 * to the `customer.addresses` cache if it has a name.
 */

export const addAddress = (
  result: Pick<Mutation, 'addAddress'>,
  _args: AddAddressInput,
  cache: Cache,
) => {
  const response = result.addAddress;

  // Ignore unsuccessful mutations.
  if (response.__typename !== 'AddAddressSuccess') return;

  // Ignore provisional addresses.
  if (!response.address?.name) return;

  updateCacheWithAddressChanges(cache, response);
};

export const addAddressV2 = (
  result: Pick<Mutation, 'addAddressV2'>,
  _args: unknown,
  cache: Cache,
) => {
  const response = result.addAddressV2;

  // Ignore unsuccessful mutations.
  if (response.__typename !== 'AddAddressSuccess') return;

  // Ignore provisional addresses.
  if (!response.address?.name) return;

  updateCacheWithAddressChanges(cache, response);
};

export const updateAddress = (
  result: Pick<Mutation, 'updateAddress'>,
  _args: AddAddressInput,
  cache: Cache,
) => {
  const response = result.updateAddress;

  // Ignore unsuccessful mutations.
  if (response.__typename !== 'UpdateAddressSuccess') return;

  // Ignore provisional addresses.
  if (!response.address?.name) return;

  updateCacheWithAddressChanges(cache, response);
};

export const updateAddressV2 = (
  result: Pick<Mutation, 'updateAddressV2'>,
  _args: unknown,
  cache: Cache,
) => {
  const response = result.updateAddressV2;

  // Ignore unsuccessful mutations.
  if (response.__typename !== 'UpdateAddressSuccess') return;

  // Ignore provisional addresses.
  if (!response.address?.name) return;

  updateCacheWithAddressChanges(cache, response);
};

export const deleteAddress = (
  result: Pick<Mutation, 'deleteAddress'>,
  args: Readonly<{ addressId: string }>,
  cache: Cache,
) => {
  const deletedId = args?.addressId;
  const response = result.deleteAddress;

  // Ignore unsuccessful mutations.
  if (!deletedId || response.__typename !== 'DeleteAddressSuccess' || !response.success) return;

  updateCacheWithAddressChanges(cache, {
    address: { id: String(deletedId) },
    removed: true,
  });
};

const updateCacheWithAddressChanges = (customerCache: Cache, addressChanges: AddressChanges) => {
  customerCache.updateQuery({ query: CurrentCustomerDocument }, (data) => {
    if (!data) return data;

    // Get cached addresses.
    const cachedCustomer = data?.currentCustomer as Customer;
    const addresses = cachedCustomer?.addresses ?? [];
    const withoutUpdated = addresses.filter(({ id }) => id !== addressChanges.address.id);

    // If the address was removed, remove it from the cache.
    if (addressChanges.removed) {
      data.currentCustomer = {
        ...cachedCustomer,
        addresses: withoutUpdated,
      };

      return data;
    }

    // If the address was added, add it and sort by id.
    data.currentCustomer = {
      ...cachedCustomer,
      addresses: [...withoutUpdated, addressChanges.address as Address].sort((c1, c2) =>
        c1.id.localeCompare(c2.id),
      ),
    };

    return data;
  });
};

type AddressChanges = Readonly<{
  address: Partial<Address>;
  removed?: boolean;
}>;
